import { BrowserProvider, type Eip1193Provider } from 'ethers'
import type { Connector } from 'wagmi'

interface ProviderOptions {
  connector?: Connector
  isFarcasterContext?: boolean
}

/**
 * AGC: Resolve a safe BrowserProvider that respects Farcaster/Base App embedded wallet context.
 * AGC: CRITICAL v5.0.4: Always use connector provider if available (avoids MetaMask in Base App)
 * AGC: Falls back to window.ethereum ONLY if no connector provided.
 */
export const getSafeBrowserProvider = async ({
  connector,
  isFarcasterContext
}: ProviderOptions): Promise<BrowserProvider> => {
  // AGC: CRITICAL FIX v5.0.4: If connector is provided, ALWAYS use its provider
  // AGC: This ensures Base App uses the embedded wallet, not MetaMask extension
  if (connector) {
    console.log('🔒 AGC: Using wagmi connector provider:', connector.name, connector.id)
    
    if (typeof connector.getProvider !== 'function') {
      throw new Error(`Connector ${connector.name} missing getProvider implementation.`)
    }

    // AGC: Add timeout to prevent infinite hanging (15 seconds)
    const getProviderPromise = connector.getProvider() as Promise<Eip1193Provider | null>
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => reject(new Error(`Connector ${connector.name} getProvider() timed out after 15 seconds`)), 15000)
    })

    const connectorProvider = (await Promise.race([getProviderPromise, timeoutPromise])) as Eip1193Provider | null

    if (!connectorProvider) {
      throw new Error(`Connector ${connector.name} provider unavailable. Try reconnecting your wallet.`)
    }

    return new BrowserProvider(connectorProvider)
  }

  // AGC: Legacy fallback for desktop mode without connector (should rarely happen)
  if (!isFarcasterContext && typeof window !== 'undefined' && window.ethereum) {
    console.warn('⚠️ AGC: Using window.ethereum fallback (no connector provided)')
    return new BrowserProvider(window.ethereum as Eip1193Provider)
  }

  throw new Error('Wallet provider not detected. Please connect your wallet.')
}

/**
 * AGC: Build signer instance and enforce that it matches the expected player address.
 * AGC: Throws descriptive error when wallets are mismatched.
 */
export const getSafeSigner = async (
  options: ProviderOptions & { expectedAddress?: string }
) => {
  const provider = await getSafeBrowserProvider(options)
  const signer = await provider.getSigner()

  if (options.expectedAddress) {
    const signerAddress = (await signer.getAddress()).toLowerCase()
    if (signerAddress !== options.expectedAddress.toLowerCase()) {
      throw new Error(
        `Wallet mismatch detected. Connected ${signerAddress} but expected ${options.expectedAddress}.`
      )
    }
  }

  return signer
}


